// Hält Gerät, Anrufzustand, Warnungen, Anrufnotiz und Power Dialer für die ganze App.
// Der Server führt den Anruf (Konferenz-Hub oder, wenn „direkt verbinden“ an ist, <Dial><Number>); diese Seite
// verbindet nur die Nutzerleitung (WebRTC), misst Ton und Qualität und zeigt den Stand aus den
// Echtzeit-Ereignissen (call.updated / call.ended). Es gibt kein Fenster: Anzeige und Bedienung sind die Leiste
// oben (DialerLeiste) und die Anrufkarte im Verlauf (AnrufKarte). „Nächster Anruf“ = dialerWeiter().
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import type { Call } from "@twilio/voice-sdk";
import { ApiFehler, fehlerText, type Anruf, type Ereignis, type PreflightErgebnis } from "../api";
import { useEchtzeit } from "../api/echtzeit";
import { useAuth } from "../auth/AuthContext";
import { vorladen as leadVorladen } from "../daten/leadCache";
import { tel } from "./api";
import { desktopTelefonStatus, mikrofonHilfeBeiSperre } from "./desktop";
import { PegelMesser, TelefonEngine, verbindungsFehlerText, type GeraetEreignis } from "./engine";
import {
  LeisePegelErkennung,
  RegistrierPlan,
  StilleErkennung,
  istTokenFehler,
  nurTasten,
  registrierAktion,
  warnungText,
  zusammenfassen,
  type Probe,
} from "./logik";
import { setzeMikrofonDb } from "./pegel";
import { EchtTonErkennung, Rufton, istEigenerTon, klangZu, netztonStumm, ruftonSoll, waehltonLesen } from "./rufton";
import { ruftonFaktor, signalAusgangSetzen, signalSpielen } from "./signalton";
import type {
  AktiverAnruf,
  AnrufOptionen,
  DialerNaechster,
  DialerStartOptionen,
  DialerZustand,
  EingehenderAnruf,
  Nacharbeit,
  PreflightLauf,
  StatusInfo,
  TelefonieSchnittstelle,
  TelefonieZustand,
  Warnung,
} from "./typen";

type Phase = "verbindet" | "klingelt" | "im_gespraech" | "beendet" | null;

const LEER: TelefonieSchnittstelle = {
  status: "nicht_eingerichtet",
  grund: "Telefonie ist noch nicht eingerichtet.",
  aktiverAnruf: null,
  fehler: null,
  anrufen: async () => undefined,
  auflegen: async () => undefined,
  stumm: () => undefined,
  info: null,
  warnungen: [],
  pegel: { ein: 0, aus: 0 },
  nacharbeit: null,
  beendetSchliessen: () => undefined,
  ergebnisSetzen: async () => undefined,
  notiz: "",
  setNotiz: () => undefined,
  tasten: async () => undefined,
  aufsHandy: async () => undefined,
  neuVerbinden: async () => undefined,
  preflight: { laeuft: false, ergebnis: null, fehler: null },
  preflightStarten: async () => undefined,
  dialer: null,
  dialerStarten: async () => undefined,
  dialerWeiter: async () => undefined,
  dialerPause: () => undefined,
  dialerBeenden: async () => undefined,
  panelOffen: false,
  setPanelOffen: () => undefined,
  neuLaden: async () => undefined,
  geraeteSetzen: async () => undefined,
  geraetBereit: false,
  fehlerZuruecksetzen: () => undefined,
  eingehend: [],
  eingehendAnnehmen: async () => undefined,
  eingehendAblehnen: () => undefined,
  eingehendIgnorieren: () => undefined,
};

export const TelefonieKontext = createContext<TelefonieSchnittstelle>(LEER);

/** Brücke für Code außerhalb von React (Smart-View-Kopf, Menüs): der Provider trägt sich hier ein. */
let starter: ((viewId: string, optionen?: DialerStartOptionen | number | null) => Promise<void>) | null = null;

/**
 * Power Dialer für eine Smart View starten und sofort den ersten Lead wählen.
 *   modus "weiter"   (Standard): nur noch nicht erreichte Leads („Weiter anrufen“)
 *   modus "von_vorn": alle Leads der Ansicht neu („Von vorn beginnen“)
 * Geht überall, auch aus Klick-Handlern ohne Hook (der TelefonieProvider muss in der App stehen).
 */
export function dialerStarten(
  viewId: string,
  modus: "weiter" | "von_vorn" = "weiter",
  optionen: Omit<DialerStartOptionen, "modus"> | number | null = {},
): Promise<void> {
  // Dritter Parameter: Optionen ({ kurz: true } = kurze Klingeldauer 20 s) oder, wie früher, die Klingeldauer in Sekunden
  const o = typeof optionen === "number" ? { klingeldauer: optionen } : (optionen ?? {});
  return starter ? starter(viewId, { ...o, modus }) : Promise.resolve();
}

/** Fehlertext für die Leiste; bei gesperrtem Mikrofon zeigt die Desktop-App zusätzlich ihren Erklär-Dialog. */
function fehlerMitHilfe(e: unknown): string {
  mikrofonHilfeBeiSperre(e);
  return verbindungsFehlerText(e);
}

function grundAus(info: StatusInfo | null, angemeldet: boolean): string | null {
  if (!angemeldet) return "Nicht angemeldet.";
  if (!info) return "Telefonie wird geladen.";
  if (!info.enabled) {
    if (info.reason === "kein_recht") return "Telefonieren ist für deine Rolle nicht freigegeben.";
    if (info.reason === "zugang_unlesbar") return "Der Twilio-Zugang lässt sich nicht lesen. Bitte neu eintragen.";
    return "Telefonie ist noch nicht eingerichtet.";
  }
  if (!info.caller_id) return "Keine Absendernummer eingetragen (Telefon > Einstellungen).";
  return null;
}

function textZuHtml(text: string): string {
  const sicher = text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  return sicher
    .split(/\n{2,}/)
    .map((absatz) => `<p>${absatz.replace(/\n/g, "<br>")}</p>`)
    .join("");
}

export function TelefonieProvider({ children }: { children: ReactNode }) {
  const { nutzer } = useAuth();
  const [info, setInfo] = useState<StatusInfo | null>(null);
  const [phase, setPhase] = useState<Phase>(null);
  const [anruf, setAnrufState] = useState<AktiverAnruf | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [warnungen, setWarnungen] = useState<Warnung[]>([]);
  const [pegel, setPegel] = useState({ ein: 0, aus: 0 });
  const [nacharbeit, setNacharbeit] = useState<Nacharbeit | null>(null);
  const navigate = useNavigate();
  const [notiz, setNotizState] = useState("");
  const [preflight, setPreflight] = useState<PreflightLauf>({ laeuft: false, ergebnis: null, fehler: null });
  const [dialer, setDialerState] = useState<DialerZustand | null>(null);
  const [panelOffen, setPanelOffen] = useState(false);
  const [geraetBereit, setGeraetBereit] = useState(false);
  const [eingehend, setEingehend] = useState<EingehenderAnruf[]>([]);

  // Rufton beim Wählen: lokales Freizeichen, solange nichts Echtes vom Netz kommt (siehe rufton.ts)
  const [leitungOffen, setLeitungOffen] = useState(false);
  const [netzKlingelt, setNetzKlingelt] = useState(false);
  const [klingelt, setKlingelt] = useState(false);
  const [abgehoben, setAbgehoben] = useState(false); // Direktmodus: Twilio „accept“ = der Kunde hat abgenommen
  const [echterTon, setEchterTon] = useState(false);
  const ruftonRef = useRef<Rufton | null>(null);
  const echtTonRef = useRef(new EchtTonErkennung());
  // Netz-Piepen bei eigenem Wählton: Soll die Gegenstelle gerade stumm sein? Und regelt der Server das schon (kein Early Media)?
  const netzStummRef = useRef(false);
  const serverOhneEarlyMediaRef = useRef(false);

  const engineRef = useRef<TelefonEngine | null>(null);
  const callRef = useRef<Call | null>(null);
  // Alle klingelnden Anrufe (Schlüssel = EingehenderAnruf.id), in der Reihenfolge des Eintreffens
  const eingehendRef = useRef(new Map<string, { info: EingehenderAnruf; twCall: Call }>());
  const eingehendZaehler = useRef(0);
  const anrufRef = useRef<AktiverAnruf | null>(null);
  const notizRef = useRef("");
  const dialerRef = useRef<DialerZustand | null>(null);
  const erwartetesEnde = useRef(false);
  const abgeschlossen = useRef<string | null>(null);
  const probenRef = useRef<Probe[]>([]);
  const alleProbenRef = useRef<Probe[]>([]);
  const zeitleisteRef = useRef<{ ts: string; name: string; aktiv: boolean }[]>([]);
  const reconnectsRef = useRef(0);
  const neuVersucheRef = useRef(0);
  const messerRef = useRef<PegelMesser | null>(null);
  const stilleRef = useRef(new StilleErkennung());
  const leiseRef = useRef(new LeisePegelErkennung());
  const weiterLaeuft = useRef(false);
  const notizGespeichert = useRef<{ id: string; text: string } | null>(null);
  const letzterPegelDb = useRef<number | null>(null);
  const takteRef = useRef<number[]>([]);
  const pegelZeit = useRef(0);
  const vorladenRef = useRef<() => Promise<void>>(async () => undefined);
  const warnungenRef = useRef<Warnung[]>([]);
  const geraetEreignisRef = useRef<(e: GeraetEreignis) => void>(() => undefined);

  const setzeAnruf = useCallback((a: AktiverAnruf | null) => {
    anrufRef.current = a;
    setAnrufState(a);
  }, []);
  const aendereAnruf = useCallback(
    (teil: Partial<AktiverAnruf>) => {
      if (!anrufRef.current) return;
      setzeAnruf({ ...anrufRef.current, ...teil });
    },
    [setzeAnruf],
  );
  const setNotiz = useCallback((t: string) => {
    notizRef.current = t;
    setNotizState(t);
  }, []);
  const setzeDialer = useCallback((d: DialerZustand | null) => {
    dialerRef.current = d;
    setDialerState(d);
  }, []);

  // ---- Status --------------------------------------------------------------
  const neuLaden = useCallback(async () => {
    if (!nutzer) {
      setInfo(null);
      return;
    }
    try {
      setInfo(await tel.status());
    } catch {
      setInfo(null);
    }
  }, [nutzer]);

  /** Wie neuLaden, aber ein Fehlschlag (Netz noch nicht wieder da) lässt den letzten guten Stand stehen. */
  const neuLadenBehalten = useCallback(async () => {
    if (!nutzer) return;
    try {
      setInfo(await tel.status());
    } catch {
      /* alter Stand bleibt */
    }
  }, [nutzer]);

  useEffect(() => {
    void neuLaden();
    if (!nutzer) return;
    const t = window.setInterval(() => void neuLaden(), 5 * 60_000);
    return () => window.clearInterval(t);
  }, [neuLaden, nutzer]);

  // Offener Anruf nach dem Neuladen der Seite oder einem Neustart des Dienstes: wieder aufnehmen (Neu verbinden,
  // Aufs Handy holen oder Auflegen), statt beim nächsten Wählen „Es läuft schon ein Anruf“ ohne Ausweg zu zeigen.
  const offenenAnrufLaden = useCallback(async (): Promise<boolean> => {
    if (anrufRef.current) return true;
    try {
      const { call } = await tel.aktiverAnruf();
      if (!call || anrufRef.current) return !!anrufRef.current;
      const c = call as Anruf & { lead_name?: string | null; contact_name?: string | null; state?: string; direkt?: boolean };
      setzeAnruf({
        callId: c.id,
        nummer: c.remote_number ?? "",
        leadId: c.lead_id ?? "",
        contactId: c.contact_id ?? null,
        beginn: c.answered_at ?? null,
        angenommenUm: c.answered_at ?? null,
        stumm: false,
        leadName: c.lead_name ?? null,
        kontaktName: c.contact_name ?? null,
        serverZustand: c.state,
        leitungWeg: c.state !== "auf_handy",
        direkt: !!c.direkt,
        meldung: c.direkt ? "Es gibt noch einen offenen Anruf. Auflegen." : "Es gibt noch einen offenen Anruf. Neu verbinden oder auflegen.",
      });
      setPhase(c.answered_at ? "im_gespraech" : c.state === "ringing" ? "klingelt" : "verbindet");
      return true;
    } catch {
      return false;
    }
  }, [setzeAnruf]);

  useEffect(() => {
    if (nutzer && info?.enabled) void offenenAnrufLaden();
  }, [nutzer, info?.enabled, offenenAnrufLaden]);

  useEffect(() => {
    if (!nutzer || !info?.enabled) return;
    tel
      .dialerAktiv()
      .then(({ sitzung }) => {
        if (sitzung && !dialerRef.current) setzeDialer({ sitzung, pausiert: true, meldung: "Power Dialer pausiert.", aktuell: null, vorgeladen: null });
      })
      .catch(() => undefined);
  }, [nutzer, info?.enabled, setzeDialer]);

  // ---- Warnungen -----------------------------------------------------------
  const warnung = useCallback((name: string, aktiv: boolean) => {
    const vorhanden = warnungenRef.current.some((w) => w.name === name);
    if (aktiv === vorhanden) return;
    zeitleisteRef.current.push({ ts: new Date().toISOString(), name, aktiv });
    const neu = aktiv
      ? [...warnungenRef.current, { name, ...warnungText(name), seit: Date.now() }]
      : warnungenRef.current.filter((w) => w.name !== name);
    warnungenRef.current = neu;
    setWarnungen(neu);
  }, []);

  // ---- Engine --------------------------------------------------------------
  const engine = useCallback((): TelefonEngine => {
    if (!engineRef.current) {
      const e = new TelefonEngine(async () => {
        const t = await tel.token();
        return { token: t.token, edge: t.edge, ttl: t.ttl };
      });
      e.geraeteWechsel = (verloren) => {
        const audio = e.device?.audio;
        if (verloren.some((g) => g.kind === "audioinput")) {
          void audio?.setInputDevice("default").catch(() => undefined);
          warnung("mikrofon-verloren", true);
          window.setTimeout(() => warnung("mikrofon-verloren", false), 8000);
        }
        if (verloren.some((g) => g.kind === "audiooutput")) {
          warnung("lautsprecher-verloren", true);
          window.setTimeout(() => warnung("lautsprecher-verloren", false), 8000);
        }
      };
      e.beiGeraetEreignis = (ereignis) => geraetEreignisRef.current(ereignis);
      engineRef.current = e;
    }
    return engineRef.current;
  }, [warnung]);

  const geraetVorbereiten = useCallback(async () => {
    const e = engine();
    const geraet = await e.bereit();
    setGeraetBereit(true);
    try {
      const einst = await tel.einstellungenLesen();
      const audio = geraet.audio;
      if (audio && einst.input_device_id) {
        const passend =
          audio.availableInputDevices.get(einst.input_device_id) ??
          [...audio.availableInputDevices.values()].find((g) => g.label && g.label === einst.input_device_label);
        if (passend) await audio.setInputDevice(passend.deviceId).catch(() => undefined);
      }
      if (audio?.isOutputSelectionSupported && einst.output_device_id) {
        const passend =
          audio.availableOutputDevices.get(einst.output_device_id) ??
          [...audio.availableOutputDevices.values()].find((g) => g.label && g.label === einst.output_device_label);
        if (passend) await audio.speakerDevices.set(passend.deviceId).catch(() => undefined);
      }
    } catch {
      /* Standardgeräte */
    }
    return geraet;
  }, [engine]);

  const geraeteSetzen = useCallback(
    async (eingang: string | null, ausgang: string | null) => {
      const geraet = await geraetVorbereiten();
      const audio = geraet.audio;
      const aenderung: Record<string, string | null> = {};
      if (audio && eingang) {
        await audio.setInputDevice(eingang);
        aenderung.input_device_id = eingang;
        aenderung.input_device_label = audio.availableInputDevices.get(eingang)?.label ?? null;
      }
      if (audio && ausgang && audio.isOutputSelectionSupported) {
        await audio.speakerDevices.set(ausgang);
        aenderung.output_device_id = ausgang;
        aenderung.output_device_label = audio.availableOutputDevices.get(ausgang)?.label ?? null;
      }
      if (Object.keys(aenderung).length) await tel.einstellungenAendern(aenderung);
    },
    [geraetVorbereiten],
  );

  // ---- Qualität ------------------------------------------------------------
  const probenSenden = useCallback(async (callId: string, ende: boolean) => {
    const proben = probenRef.current;
    probenRef.current = [];
    const summary = ende
      ? {
          ...zusammenfassen(alleProbenRef.current),
          edge: engineRef.current?.device?.edge ?? null,
          codec: callRef.current?.codec || null,
          warnings: zeitleisteRef.current,
          reconnects: reconnectsRef.current,
          eingang: engineRef.current?.device?.audio?.inputDevice?.label ?? null,
          app: "web",
          user_agent: navigator.userAgent.slice(0, 200),
        }
      : undefined;
    if (!proben.length && !summary) return;
    try {
      await tel.qualitaetMelden(callId, proben as never, summary);
    } catch {
      /* Messwerte sind nicht kritisch */
    }
  }, []);

  const monitorStoppen = useCallback(() => {
    takteRef.current.forEach((t) => window.clearInterval(t));
    takteRef.current = [];
    messerRef.current?.stoppen();
    messerRef.current = null;
    stilleRef.current.zuruecksetzen();
    leiseRef.current.zuruecksetzen();
    warnung("mikrofon-stille", false);
    warnung("mikrofon-leise", false);
    setzeMikrofonDb(null);
    setPegel({ ein: 0, aus: 0 });
  }, [warnung]);

  const monitorStarten = useCallback(
    (twCall: Call) => {
      monitorStoppen();
      const messer = new PegelMesser(() => twCall.getLocalStream());
      messerRef.current = messer;
      const takt = window.setInterval(() => {
        const db = messer.messen();
        letzterPegelDb.current = db;
        if (db === null) return;
        setzeMikrofonDb(db);
        const pruefen = callRef.current === twCall && !twCall.isMuted() && twCall.status() === "open";
        const wechsel = stilleRef.current.probe(db, Date.now(), pruefen);
        if (wechsel === "stille") warnung("mikrofon-stille", true);
        if (wechsel === "ok") warnung("mikrofon-stille", false);
        // „zu leise“ (unter -50 dBFS) nur, solange nicht schon die Stille-Warnung (rot) steht
        const leise = leiseRef.current.probe(db, Date.now(), pruefen && !stilleRef.current.aktiv);
        if (leise === "leise") warnung("mikrofon-leise", true);
        if (leise === "ok") warnung("mikrofon-leise", false);
      }, 250);
      takteRef.current.push(takt);
    },
    [monitorStoppen, warnung],
  );

  // ---- Notiz zum Anruf (während und nach dem Gespräch automatisch gespeichert) -----------------
  const notizSpeichern = useCallback(async (callId: string, text: string) => {
    const stand = notizGespeichert.current;
    if (stand?.id === callId && stand.text === text) return;
    if (!stand?.id || stand.id !== callId) {
      if (!text) return; // nichts getippt, nichts zu speichern
    }
    notizGespeichert.current = { id: callId, text };
    try {
      await tel.anrufNotiz(callId, { note_html: text ? textZuHtml(text) : "" });
    } catch (e) {
      notizGespeichert.current = stand ?? null;
      setFehler(`Notiz nicht gespeichert: ${fehlerText(e)}`);
    }
  }, []);

  useEffect(() => {
    const id = anruf?.callId ?? nacharbeit?.call.id;
    if (!id) return;
    const t = window.setTimeout(() => void notizSpeichern(id, notiz), 900);
    return () => window.clearTimeout(t);
  }, [notiz, anruf?.callId, nacharbeit?.call.id, notizSpeichern]);

  // ---- Abschluss -----------------------------------------------------------
  // Der Anruf ist für die Oberfläche SOFORT frei (anrufRef leer), damit „Nächster Anruf“ ohne Wartezeit wählen kann;
  // den Endstand (Dauer, Aktivität) holt die Karte danach nach.
  const abschluss = useCallback(
    async (callId: string) => {
      const a = anrufRef.current;
      if (abgeschlossen.current === callId || !a || a.callId !== callId) return;
      abgeschlossen.current = callId;
      erwartetesEnde.current = true;
      try {
        callRef.current?.disconnect();
      } catch {
        /* schon getrennt */
      }
      callRef.current = null;
      monitorStoppen();
      void probenSenden(callId, true);
      warnungenRef.current = [];
      setWarnungen([]);
      setzeAnruf(null);
      const dauer = a.angenommenUm ? Math.max(0, Math.round((Date.now() - new Date(a.angenommenUm).getTime()) / 1000)) : 0;
      const vorlaeufig = {
        id: callId,
        lead_id: a.leadId,
        contact_id: a.contactId ?? null,
        direction: a.eingehend ? "inbound" : "outbound",
        answered_at: a.angenommenUm ?? null,
        ended_at: new Date().toISOString(),
        duration: dauer,
        disposition: null,
        activity_id: null,
        close_sync_status: "pending",
      } as unknown as Anruf;
      signalSpielen("auflegen"); // kurzer, tiefer, fallender Ton (signalton.ts); ein nächster Wählton wartet dahinter
      setNacharbeit({
        call: vorlaeufig,
        leadId: a.leadId,
        contactId: a.contactId ?? null,
        leadName: a.leadName ?? null,
        kontaktName: a.kontaktName ?? null,
        notiz: notizRef.current,
        dialer: !!a.dialer,
      });
      setPhase("beendet");
      void notizSpeichern(callId, notizRef.current);
      try {
        const { call } = await tel.anruf(callId);
        setNacharbeit((n) => (n && n.call.id === callId ? { ...n, call } : n));
      } catch {
        /* die Karte zeigt dann den vorläufigen Stand */
      }
    },
    [monitorStoppen, notizSpeichern, probenSenden, setzeAnruf],
  );

  const serverStand = useCallback(
    (daten: Record<string, unknown>) => {
      const a = anrufRef.current;
      if (!a?.callId) return;
      const state = (daten.state as string | undefined) ?? undefined;
      const teil: Partial<AktiverAnruf> = {};
      if (state) teil.serverZustand = state;
      if (typeof daten.meldung === "string") teil.meldung = daten.meldung;
      if (typeof daten.handy === "string") teil.handy = daten.handy;
      if (state === "ringing") setPhase("klingelt");
      if (state === "in-progress") {
        setPhase("im_gespraech");
        teil.angenommenUm = (daten.answered_at as string | undefined) ?? a.angenommenUm ?? new Date().toISOString();
        teil.beginn = a.beginn ?? teil.angenommenUm ?? null;
        if (daten.wieder_verbunden) {
          teil.leitungWeg = false;
          teil.meldung = null;
        }
      }
      if (state === "nutzer_getrennt") {
        teil.leitungWeg = true;
        teil.meldung = `Verbindung abgerissen. Der Kunde wartet noch bis zu ${daten.rettung_sekunden ?? 60} Sekunden.`;
      }
      if (state === "auf_handy") {
        erwartetesEnde.current = true;
        try {
          callRef.current?.disconnect();
        } catch {
          /* egal */
        }
        teil.leitungWeg = false;
        teil.handy = "verbunden";
        teil.meldung = "Das Gespräch läuft auf deinem Handy. Auflegen am Handy oder hier.";
        setPhase("im_gespraech");
      }
      aendereAnruf(teil);
    },
    [aendereAnruf],
  );

  useEchtzeit(["call.updated", "call.ended", "call.quality_warning"], (e: Ereignis) => {
    const a = anrufRef.current;
    const callId = e.data.call_id as string | undefined;
    if (!a?.callId || callId !== a.callId) return;
    if (e.type === "call.ended") void abschluss(a.callId);
    else if (e.type === "call.updated") serverStand(e.data);
  });

  // Endstand des beendeten Anrufs (Dauer, Aktivität, Ergebnis) nachladen, sobald der Server das Ende meldet
  useEchtzeit(["call.ended"], (e: Ereignis) => {
    const callId = e.data.call_id as string | undefined;
    if (!callId) return;
    tel
      .anruf(callId)
      .then(({ call }) => setNacharbeit((n) => (n && n.call.id === callId ? { ...n, call } : n)))
      .catch(() => undefined);
  });

  // Close-Protokoll nach dem Auflegen: Die Nacharbeit zeigt "In Close protokolliert" bzw. den Fehler,
  // sobald der Server das meldet (auch nach "Erneut versuchen").
  useEchtzeit(["call.updated"], (e: Ereignis) => {
    const status = e.data.close_sync_status as Anruf["close_sync_status"] | undefined;
    const callId = e.data.call_id as string | undefined;
    if (!status || !callId) return;
    setNacharbeit((n) =>
      n && n.call.id === callId
        ? {
            ...n,
            call: {
              ...n.call,
              close_sync_status: status,
              close_sync_error: status === "error" ? ((e.data.meldung as string | undefined) ?? null) : null,
            } as Anruf,
          }
        : n,
    );
  });

  // Rückfallebene ohne Echtzeit: Stand alle 3 s abfragen, solange ein Anruf läuft
  useEffect(() => {
    if (!anruf?.callId) return;
    const callId = anruf.callId;
    const t = window.setInterval(async () => {
      try {
        const { call } = await tel.anruf(callId);
        const c = call as Anruf & { state?: string; answered_at?: string | null };
        if (c.state === "beendet") void abschluss(callId);
        else if (c.state && c.state !== anrufRef.current?.serverZustand) serverStand({ state: c.state, answered_at: c.answered_at });
      } catch {
        /* nächster Versuch */
      }
    }, 3000);
    return () => window.clearInterval(t);
  }, [anruf?.callId, abschluss, serverStand]);

  // ---- Twilio-Call verdrahten ----------------------------------------------
  const verdrahten = useCallback(
    (twCall: Call, callId: string) => {
      callRef.current = twCall;
      erwartetesEnde.current = false;
      echtTonRef.current.zuruecksetzen();
      setEchterTon(false);
      setLeitungOffen(false);
      setNetzKlingelt(false);
      setKlingelt(false);
      setAbgehoben(false);
      const ruftonEnde = () => {
        setLeitungOffen(false);
        setNetzKlingelt(false);
        setKlingelt(false);
      };
      twCall.on("accept", () => {
        monitorStarten(twCall);
        setLeitungOffen(true);
        setNetzKlingelt(false); // Direktmodus: „accept“ = Angerufener hat abgenommen
        setKlingelt(false);
        if (anrufRef.current?.direkt) {
          // Direktmodus: „accept“ ist das Abheben, die Stimme des Kunden sofort hörbar machen (nicht auf den Server warten)
          setAbgehoben(true);
          netzStummRef.current = false;
          netztonStumm(twCall, false);
        } else {
          netztonStumm(twCall, netzStummRef.current);
        }
      });
      // Direktmodus (answerOnBridge): Twilio sagt, ob das Freizeichen vom Netz kommt. Bei „Netz-Freizeichen“ spielen wir
      // nur dann einen Ersatz; bei eigenem Wählton läuft er immer und das Netz-Piepen wird stumm geschaltet.
      twCall.on("ringing", (hatNetzton: boolean) => {
        setNetzKlingelt(!hatNetzton);
        setKlingelt(true);
        setAbgehoben(false); // „ringing“ kommt nie nach dem Abheben (nur die Test-Attrappe sendet „accept“ vorab)
        netztonStumm(twCall, netzStummRef.current);
      });
      twCall.on("cancel", ruftonEnde);
      twCall.on("reject", ruftonEnde);
      // War die Leitung schon offen, bevor wir lauschen (Ereignis verpasst), gilt sie jetzt als offen
      if (twCall.status() === "open") setLeitungOffen(true);
      twCall.on("volume", (ein: number, aus: number) => {
        // Kommt vor der Annahme echter Ton auf der Leitung an, gehört er allein dem Netz: lokalen Rufton aus
        if (!anrufRef.current?.angenommenUm && echtTonRef.current.probe(aus)) setEchterTon(true);
        const jetzt = Date.now();
        if (jetzt - pegelZeit.current > 120) {
          pegelZeit.current = jetzt;
          setPegel({ ein, aus });
        }
      });
      twCall.on("sample", (s: { mos: number | null; jitter: number; rtt: number; packetsLostFraction: number; codecName: string }) => {
        const probe: Probe & Record<string, unknown> = {
          ts: new Date().toISOString(),
          mos: s.mos,
          jitter: s.jitter,
          rtt: s.rtt,
          packet_loss: s.packetsLostFraction,
          warnings: warnungenRef.current.map((w) => w.name),
          audio_in_dbfs: Number.isFinite(letzterPegelDb.current ?? NaN) ? Math.round((letzterPegelDb.current as number) * 10) / 10 : null,
          codec: s.codecName,
        };
        probenRef.current.push(probe);
        alleProbenRef.current.push(probe);
        if (probenRef.current.length >= 10) void probenSenden(callId, false);
      });
      twCall.on("warning", (name: string) => warnung(name, true));
      twCall.on("warning-cleared", (name: string) => warnung(name, false));
      twCall.on("reconnecting", () => {
        reconnectsRef.current += 1;
        warnung("reconnecting", true);
      });
      twCall.on("reconnected", () => warnung("reconnecting", false));
      twCall.on("mute", (stumm: boolean) => aendereAnruf({ stumm }));
      twCall.on("error", (f: unknown) => setFehler(fehlerMitHilfe(f)));
      twCall.on("disconnect", () => {
        if (callRef.current === twCall) callRef.current = null;
        // Hat ein neuer Anruf die Leitung schon übernommen („Beenden & annehmen“), nicht dessen Anzeige und Messung stoppen
        if (!callRef.current) {
          ruftonEnde();
          monitorStoppen();
          warnung("reconnecting", false);
        }
        const a = anrufRef.current;
        if (a?.eingehend && a.callId === callId) {
          // Eingehend gibt es keine Rettung: Leitung weg = Gespräch zu Ende, Nacharbeit (Notiz) öffnen
          erwartetesEnde.current = true;
          void abschluss(callId);
          return;
        }
        if (!erwartetesEnde.current && a && a.callId === callId && abgeschlossen.current !== callId) {
          if (a.direkt) aendereAnruf({ meldung: a.meldung ?? "Die Leitung ist beendet." });
          else aendereAnruf({ leitungWeg: true, meldung: a.meldung ?? "Deine Leitung ist getrennt. Neu verbinden oder aufs Handy holen." });
        }
      });
    },
    [abschluss, aendereAnruf, monitorStarten, monitorStoppen, probenSenden, warnung],
  );

  // ---- Wählton ("Wählton wie Close") ----------------------------------------------
  // Eine Stelle entscheidet (ruftonSoll): Start, sobald die Leitung steht und der Angerufene noch nicht abgenommen hat;
  // Stopp bei Abheben, Besetzt, Auflegen, Fehler, Leitung weg oder (nur „Netz-Freizeichen“) sobald echter Ton vom Netz ankommt.
  // Bei einem eigenen Ton wird das Netz-Piepen bis zum Abheben stumm geschaltet (siehe rufton.ts, `netztonStumm`).
  useEffect(() => {
    const zustand = anruf?.serverZustand;
    const imGespraech =
      abgehoben ||
      phase === "im_gespraech" ||
      zustand === "in-progress" ||
      zustand === "auf_handy" ||
      zustand === "nutzer_getrennt";
    // Eigene Wähltöne gibt es nur im Power Dialer. Alle anderen Anrufe (Lead, Liste, Inbox, Rückruf, Nummer wählen)
    // haben das normale Netz-Freizeichen: kein lokaler Ton, Netz-Piepen nicht stumm geschaltet.
    const imDialer = !!anruf?.dialer;
    const ton = waehltonLesen();
    const klang = imDialer ? klangZu(ton) : null;
    const eigen = imDialer && istEigenerTon(ton);
    const beendet = !anruf || phase === "beendet" || phase === null || zustand === "beendet";
    const soll = ruftonSoll({
      erlaubt: klang !== null,
      ausgehend: !!anruf && !anruf.eingehend,
      direkt: !!anruf?.direkt,
      leitungOffen,
      klingeltOhneNetzton: netzKlingelt,
      klingelt,
      eigenerTon: eigen,
      imGespraech,
      beendet,
      echterTon,
    });
    if (!ruftonRef.current) {
      const ausgangId = () => {
        const id = [...(engineRef.current?.device?.audio?.speakerDevices.get() ?? [])][0]?.deviceId;
        return id && id !== "default" ? id : null;
      };
      ruftonRef.current = new Rufton();
      ruftonRef.current.ausgangId = ausgangId;
      ruftonRef.current.verstaerkung = ruftonFaktor;
      signalAusgangSetzen(ausgangId);
    }
    if (soll && klang) void ruftonRef.current.start(klang);
    else ruftonRef.current.stopp();

    // Netz-Piepen: nur bei eigenem Ton, nur bis zum Abheben, nur wenn der Server es nicht schon selbst abschaltet
    const stumm = eigen && !serverOhneEarlyMediaRef.current && !!anruf && !anruf.eingehend && !imGespraech && !beendet;
    netzStummRef.current = stumm;
    netztonStumm(callRef.current, stumm);
    if (!stumm) return;
    // Die Gegenstelle kann erst nach dem Aufbau eine Audiospur haben: regelmäßig nachziehen
    const t = window.setInterval(() => netztonStumm(callRef.current, true), 250);
    return () => window.clearInterval(t);
  }, [anruf, phase, leitungOffen, netzKlingelt, klingelt, abgehoben, echterTon]);

  useEffect(() => () => ruftonRef.current?.stopp(), []);

  // ---- Eingehende Anrufe ---------------------------------------------------
  // Das Gerät meldet sich bei Twilio an (register) und klingelt, solange die Seite offen ist. Der Server gibt Lead,
  // Status und Routing-Grund als Parameter des Anrufs mit; angenommen wird mit der Karte (EingehendKarte).
  const eingehendAbgleichen = useCallback(() => {
    setEingehend([...eingehendRef.current.values()].map((x) => x.info));
  }, []);
  const eingehendEntfernen = useCallback(
    (id: string) => {
      if (eingehendRef.current.delete(id)) eingehendAbgleichen();
    },
    [eingehendAbgleichen],
  );
  const eingehendFinden = (id?: string) => (id ? eingehendRef.current.get(id) : eingehendRef.current.values().next().value) ?? null;

  const beiEingang = useCallback(
    (twCall: Call) => {
      const p = twCall.customParameters;
      const wert = (k: string) => p.get(k) || null;
      // Mehrere gleichzeitige Anrufe sind erlaubt (je eine Zeile in der Leiste); auch während eines Gesprächs
      eingehendZaehler.current += 1;
      const callId = wert("call_id") ?? "";
      const id = callId && !eingehendRef.current.has(callId) ? callId : `${callId || "tw"}-${eingehendZaehler.current}`;
      const info: EingehenderAnruf = {
        id,
        callId,
        leadId: wert("lead_id") ?? "",
        leadName: wert("lead_name"),
        leadStatus: wert("lead_status"),
        kontaktName: wert("kontakt_name"),
        contactId: wert("contact_id"),
        nummer: wert("nummer") ?? "",
        anonym: !!wert("anonym"),
        grund: wert("grund"),
        angerufen: wert("angerufen"),
        angerufenName: wert("angerufen_name"),
        mehrere: !!wert("mehrere"),
        seit: Date.now(),
      };
      eingehendRef.current.set(id, { info, twCall });
      eingehendAbgleichen();
      const weg = () => {
        if (eingehendRef.current.get(id)?.twCall === twCall) eingehendEntfernen(id);
      };
      twCall.on("cancel", weg);
      twCall.on("reject", weg);
      twCall.on("disconnect", weg);
    },
    [eingehendAbgleichen, eingehendEntfernen],
  );

  // ---- Registrierung für eingehende Anrufe halten ---------------------------
  // Ein Anstoß (Einstellung geändert, Ruhezustand, Netz zurück, Gerät abgemeldet oder Fehler, Prüftakt) wird entprellt
  // und läuft nie doppelt (RegistrierPlan). Ein Versuch prüft den Gerätezustand, erneuert bei Bedarf das Token und
  // meldet das Gerät an; scheitert er, kommt der nächste mit wachsendem Abstand.
  const planRef = useRef(new RegistrierPlan());
  const registrierTimer = useRef<number | null>(null);
  const registrierOpt = useRef({ erzwingen: false, token: false });
  const gewolltRef = useRef(false);
  const ausfuehrenRef = useRef<() => Promise<void>>(async () => undefined);

  const registrierenAnfordern = useCallback((o: { sofort?: boolean; erzwingen?: boolean; token?: boolean } = {}) => {
    if (!gewolltRef.current) return;
    const plan = planRef.current;
    const erzwingen = !!o.erzwingen && plan.erzwingenErlaubt(Date.now());
    registrierOpt.current = {
      erzwingen: registrierOpt.current.erzwingen || erzwingen,
      token: registrierOpt.current.token || !!o.token,
    };
    const planung = plan.anfordern(!!o.sofort);
    if (planung.aktion === "planen") {
      registrierTimer.current = window.setTimeout(() => void ausfuehrenRef.current(), planung.inMs);
    }
  }, []);

  ausfuehrenRef.current = async () => {
    registrierTimer.current = null;
    const plan = planRef.current;
    if (!plan.starten()) return;
    const opt = registrierOpt.current;
    registrierOpt.current = { erzwingen: false, token: false };
    let ok = true;
    try {
      // Ohne Netz gibt es nichts zu tun: das Ereignis „online“ stößt es wieder an
      if (gewolltRef.current && navigator.onLine !== false) {
        const e = engine();
        if (!e.device || e.device.state === "destroyed") await geraetVorbereiten();
        await e.registrieren(beiEingang, {
          erzwingen: opt.erzwingen,
          belegt: !!anrufRef.current || eingehendRef.current.size > 0,
          tokenErneuern: opt.token,
        });
      }
    } catch {
      ok = false; // Anmeldung nicht möglich (z. B. Netz): ausgehende Anrufe gehen trotzdem, nächster Versuch folgt
    }
    plan.beenden(ok);
    if (!ok) registrierenAnfordern();
  };

  geraetEreignisRef.current = (ereignis) => {
    const zustand = engineRef.current?.device?.state;
    if (ereignis.art === "registered") {
      planRef.current.registriert();
      desktopTelefonStatus(true);
    } else if (ereignis.art === "unregistered") {
      if (ereignis.absichtlich) return;
      desktopTelefonStatus(false);
      registrierenAnfordern();
    } else if (ereignis.art === "error") {
      desktopTelefonStatus(zustand === "registered");
      registrierenAnfordern({ token: istTokenFehler(ereignis.code) });
    } else if (ereignis.art === "destroyed" && !ereignis.absichtlich) {
      desktopTelefonStatus(false);
      registrierenAnfordern();
    }
  };

  useEffect(() => {
    const gewollt = !!(nutzer && info?.enabled && info.browser_incoming && info.caller_id);
    gewolltRef.current = gewollt;
    if (gewollt) {
      registrierenAnfordern({ sofort: true });
    } else if (nutzer && info) {
      // Kein Browser-Telefon nötig (aus oder nicht eingerichtet): für die Desktop-App ist das kein Verbindungsfehler
      desktopTelefonStatus(true);
    }
  }, [nutzer, info?.enabled, info?.browser_incoming, info?.caller_id, registrierenAnfordern]);

  useEffect(
    () => () => {
      if (registrierTimer.current !== null) window.clearTimeout(registrierTimer.current);
      registrierTimer.current = null;
      planRef.current.zuruecksetzen();
      gewolltRef.current = false;
    },
    [nutzer],
  );

  // Netz zurück: Status neu lesen und das Gerät neu anmelden (der WebSocket kann tot sein). Tab wieder sichtbar und
  // ein Prüftakt fangen ab, was kein Ereignis gemeldet hat (Gerät fehlt oder ist nicht angemeldet).
  useEffect(() => {
    if (!nutzer) return;
    const online = () => {
      void neuLadenBehalten();
      registrierenAnfordern({ erzwingen: true });
    };
    const pruefen = () => {
      if (!gewolltRef.current || navigator.onLine === false) return;
      if (registrierAktion(engineRef.current?.device?.state) !== "nichts") registrierenAnfordern();
    };
    const sichtbar = () => {
      if (document.visibilityState === "visible") pruefen();
    };
    window.addEventListener("online", online);
    document.addEventListener("visibilitychange", sichtbar);
    const takt = window.setInterval(pruefen, 45_000);
    return () => {
      window.removeEventListener("online", online);
      document.removeEventListener("visibilitychange", sichtbar);
      window.clearInterval(takt);
    };
  }, [nutzer, neuLadenBehalten, registrierenAnfordern]);

  // Nach einem Gespräch den Zustand prüfen (Registrierung kann währenddessen abgefallen sein)
  const gespraechVorher = useRef(false);
  useEffect(() => {
    if (gespraechVorher.current && !anruf) registrierenAnfordern();
    gespraechVorher.current = !!anruf;
  }, [anruf, registrierenAnfordern]);

  // Tab-Titel blinkt, solange es klingelt
  const ersterEingang = eingehend[0] ?? null;
  const ersterId = ersterEingang?.id ?? null;
  useEffect(() => {
    if (!ersterEingang) return;
    const alt = document.title;
    let an = false;
    const t = window.setInterval(() => {
      an = !an;
      document.title = an ? `Anruf: ${ersterEingang.leadName ?? ersterEingang.kontaktName ?? (ersterEingang.anonym ? "Anonym" : ersterEingang.nummer)}` : alt;
    }, 900);
    return () => {
      window.clearInterval(t);
      document.title = alt;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ersterId]);

  const eingehendAnnehmen = useCallback(async (id?: string) => {
    const e = eingehendFinden(id);
    if (!e) return;
    eingehendEntfernen(e.info.id);
    // Läuft ein Gespräch, wird es zuerst beendet („Beenden & annehmen“), ohne Nachfrage: Notiz sichern, auflegen,
    // lokal sofort abschließen (die Nacharbeit zum alten Gespräch entfällt, der neue Anruf übernimmt die Leiste).
    const laufend = anrufRef.current;
    if (laufend) {
      erwartetesEnde.current = true;
      if (laufend.callId) {
        void tel.auflegen(laufend.callId).catch(() => undefined);
        void abschluss(laufend.callId);
      } else {
        try {
          callRef.current?.disconnect();
        } catch {
          /* schon getrennt */
        }
        callRef.current = null;
        setzeAnruf(null);
        setPhase(null);
      }
    }
    const jetztIso = new Date().toISOString();
    setFehler(null);
    setNacharbeit(null);
    setNotiz("");
    warnungenRef.current = [];
    setWarnungen([]);
    probenRef.current = [];
    alleProbenRef.current = [];
    zeitleisteRef.current = [];
    reconnectsRef.current = 0;
    abgeschlossen.current = null;
    notizGespeichert.current = null;
    setzeAnruf({
      callId: e.info.callId,
      nummer: e.info.nummer,
      leadId: e.info.leadId,
      contactId: e.info.contactId,
      beginn: jetztIso,
      angenommenUm: jetztIso,
      stumm: false,
      leadName: e.info.leadName,
      kontaktName: e.info.kontaktName,
      serverZustand: "in-progress",
      direkt: true,
      eingehend: true,
    });
    setPhase("im_gespraech");
    verdrahten(e.twCall, e.info.callId);
    try {
      e.twCall.accept();
    } catch (err) {
      setFehler(fehlerMitHilfe(err));
      setzeAnruf(null);
      setPhase(null);
    }
  }, [abschluss, eingehendEntfernen, setNotiz, setzeAnruf, verdrahten]);

  const eingehendAblehnen = useCallback(
    (id?: string) => {
      const e = eingehendFinden(id);
      if (!e) return;
      eingehendEntfernen(e.info.id);
      try {
        e.twCall.reject();
      } catch {
        /* schon beendet */
      }
    },
    [eingehendEntfernen],
  );

  // Ignorieren: nur der Klingelton hier ist aus, der Anrufer wird nicht abgewiesen (bei den anderen Geräten und in der
  // Zielliste des Servers klingelt es weiter, bis er auflegt oder die Mailbox greift).
  const eingehendIgnorieren = useCallback(
    (id?: string) => {
      const e = eingehendFinden(id);
      if (!e) return;
      eingehendEntfernen(e.info.id);
      try {
        e.twCall.ignore();
      } catch {
        /* schon beendet */
      }
    },
    [eingehendEntfernen],
  );

  // ---- Anrufen -------------------------------------------------------------
  const anrufen = useCallback(
    async (nummer: string, leadId: string, contactId?: string, opt: AnrufOptionen = {}) => {
      if (anrufRef.current) {
        setFehler("Es läuft schon ein Anruf.");
        return;
      }
      const grund = grundAus(info, !!nutzer);
      if (grund) {
        setFehler(grund);
        return;
      }
      setFehler(null);
        setNacharbeit(null);
      setNotiz("");
      warnungenRef.current = [];
      setWarnungen([]);
      probenRef.current = [];
      alleProbenRef.current = [];
      zeitleisteRef.current = [];
      reconnectsRef.current = 0;
      neuVersucheRef.current = 0;
      const neu: AktiverAnruf = {
        callId: null,
        nummer,
        leadId,
        contactId: contactId ?? null,
        beginn: null,
        stumm: false,
        leadName: opt.leadName ?? null,
        kontaktName: opt.kontaktName ?? null,
        serverZustand: "connecting",
        dialer: !!opt.dialerSitzung,
      };
      setzeAnruf(neu);
      setPhase("verbindet");
      signalSpielen("waehlen"); // sanfte steigende Tonfolge, nach einem eben gespielten Auflegton
      let start;
      try {
        start = await tel.anrufStarten({
          to: nummer,
          lead_id: leadId,
          contact_id: contactId ?? null,
          bestaetigt: true, // keine Rückfragen beim Anrufen: Klick = Anruf
          dialer_sitzung: opt.dialerSitzung ?? null,
          klingeldauer: opt.klingeldauer ?? null,
          // Eigener Wählton (nur Power Dialer): dem Server sagen, dass das Netz-Freizeichen (Early Media) nicht gebraucht wird
          early_media: opt.dialerSitzung && istEigenerTon(waehltonLesen()) ? false : null, // nur im Power Dialer; sonst Netz-Freizeichen
        });
      } catch (e) {
        setzeAnruf(null);
        setPhase(null);
        if (e instanceof ApiFehler && e.code === "bereits_im_gespraech" && (await offenenAnrufLaden())) return;
        setFehler(fehlerText(e));
        return;
      }
      const c = start.call as Anruf & { lead_name?: string | null; contact_name?: string | null; direkt?: boolean };
      // Schaltet der Server Early Media wirklich ab (Antwort sagt es), braucht es keine Stummschaltung im Browser
      serverOhneEarlyMediaRef.current = !!opt.dialerSitzung && start.early_media_wirksam === true && istEigenerTon(waehltonLesen());
      abgeschlossen.current = null;
      notizGespeichert.current = null;
      aendereAnruf({
        callId: c.id,
        nummer: c.remote_number ?? nummer,
        leadName: neu.leadName ?? c.lead_name ?? null,
        kontaktName: neu.kontaktName ?? c.contact_name ?? null,
        direkt: !!c.direkt,
      });
      if (opt.dialerSitzung) void vorladenRef.current();
      try {
        await geraetVorbereiten();
        const twCall = await engine().verbinden(start.connect_params);
        verdrahten(twCall, c.id);
      } catch (e) {
        setFehler(fehlerMitHilfe(e));
        erwartetesEnde.current = true;
        try {
          await tel.auflegen(c.id);
        } catch {
          /* Server schließt den Anruf nach 90 s selbst */
        }
        setzeAnruf(null);
        setPhase(null);
      }
    },
    [info, nutzer, setNotiz, setzeAnruf, aendereAnruf, geraetVorbereiten, engine, verdrahten, offenenAnrufLaden],
  );

  const auflegen = useCallback(async () => {
    const a = anrufRef.current;
    if (!a) return;
    erwartetesEnde.current = true;
    if (a.callId) {
      try {
        await tel.auflegen(a.callId);
      } catch (e) {
        setFehler(fehlerText(e));
      }
    }
    try {
      callRef.current?.disconnect();
    } catch {
      /* egal */
    }
    if (!a.callId) {
      setzeAnruf(null);
      setPhase(null);
    }
  }, [setzeAnruf]);

  const stumm = useCallback(
    (an: boolean) => {
      callRef.current?.mute(an);
      stilleRef.current.zuruecksetzen();
      if (an) warnung("mikrofon-stille", false);
      aendereAnruf({ stumm: an });
    },
    [aendereAnruf, warnung],
  );

  const tasten = useCallback(async (ziffern: string) => {
    const a = anrufRef.current;
    const z = nurTasten(ziffern);
    if (!a?.callId || !z) return;
    try {
      if (a.direkt) {
        // Direkt verbunden: die Töne gehen aus dem Browser durch die Leitung (kein Mischer auf dem Server)
        callRef.current?.sendDigits(z);
        return;
      }
      await tel.dtmf(a.callId, z);
    } catch (e) {
      setFehler(fehlerText(e));
    }
  }, []);

  const aufsHandy = useCallback(async () => {
    const a = anrufRef.current;
    if (!a?.callId || a.direkt) return;
    try {
      await tel.handy(a.callId);
      aendereAnruf({ handy: "wird_angerufen", meldung: "Dein Handy klingelt gleich. Mit der Taste 1 übernehmen." });
    } catch (e) {
      setFehler(fehlerText(e));
    }
  }, [aendereAnruf]);

  const neuVerbinden = useCallback(async () => {
    const a = anrufRef.current;
    if (!a?.callId || a.direkt) return;
    setFehler(null);
    const relay = neuVersucheRef.current > 0;
    neuVersucheRef.current += 1;
    try {
      let rtc: RTCConfiguration | undefined;
      if (relay) {
        const t = await tel.token(true);
        if (t.ice_servers?.length) rtc = { iceServers: t.ice_servers, iceTransportPolicy: "relay" };
      }
      const twCall = await engine().verbinden({ To: a.nummer, CallId: a.callId, Rejoin: "1" }, rtc);
      verdrahten(twCall, a.callId);
      aendereAnruf({ leitungWeg: false, meldung: relay ? "Neu verbunden (über Relay)." : null });
    } catch (e) {
      setFehler(fehlerMitHilfe(e));
    }
  }, [aendereAnruf, engine, verdrahten]);

  // ---- Beendeter Anruf und Power Dialer -------------------------------------
  const beendetSchliessen = useCallback(() => {
    setNacharbeit(null);
    setPhase(null);
    setNotiz("");
  }, [setNotiz]);

  const ergebnisSetzen = useCallback(async (disposition: string) => {
    const n = nacharbeit;
    if (!n) return;
    try {
      const { call } = await tel.anrufNotiz(n.call.id, { disposition });
      setNacharbeit((x) => (x && x.call.id === n.call.id ? { ...x, call } : x));
    } catch (e) {
      setFehler(fehlerText(e));
    }
  }, [nacharbeit]);

  const leadPfad = (id: string) => `/lead/${encodeURIComponent(id)}`;

  /** Den übernächsten Lead schon suchen, reservieren und im Cache vorladen (läuft im Hintergrund). */
  const vorladenStarten = useCallback(async () => {
    const d = dialerRef.current;
    if (!d) return;
    try {
      const n = await tel.dialerNaechster(d.sitzung.id, true);
      const jetzt = dialerRef.current;
      if (!jetzt || jetzt.sitzung.id !== d.sitzung.id) return;
      setzeDialer({ ...jetzt, sitzung: n.sitzung, vorgeladen: n.lead ? n : null });
      if (n.lead) leadVorladen(n.lead.id);
    } catch {
      /* Vorladen ist nur ein Vorsprung, „Nächster Anruf“ sucht sonst selbst */
    }
  }, [setzeDialer]);
  vorladenRef.current = vorladenStarten;

  /** „Nächster Anruf →“: auflegen (falls ein Gespräch läuft), nächsten Lead öffnen und sofort wählen. */
  const dialerWeiter = useCallback(async () => {
    const d0 = dialerRef.current;
    if (!d0 || weiterLaeuft.current) return;
    weiterLaeuft.current = true;
    try {
      const laufend = anrufRef.current;
      let aufgelegt: Promise<unknown> = Promise.resolve();
      if (laufend) {
        // Notiz sichern, auflegen und den Anruf lokal sofort abschließen; Twilio meldet das Ende nur noch nach.
        if (laufend.callId) {
          void notizSpeichern(laufend.callId, notizRef.current);
          aufgelegt = tel.auflegen(laufend.callId).catch((e) => setFehler(fehlerText(e)));
          erwartetesEnde.current = true;
          void abschluss(laufend.callId);
        } else {
          setzeAnruf(null);
          setPhase(null);
        }
      }
        const vor = d0.vorgeladen?.lead ?? null;
      setzeDialer({ ...d0, pausiert: false, meldung: null });
      if (vor) navigate(leadPfad(vor.id));
      await aufgelegt;
      let n: DialerNaechster;
      try {
        n = await tel.dialerNaechster(d0.sitzung.id);
      } catch (e) {
        if (e instanceof ApiFehler && e.code === "konflikt") {
          // Sitzung ist auf dem Server beendet (z. B. nach Neustart): ohne Sitzung weiter nichts zu tun
          setzeDialer(null);
          setFehler("Die Power-Dialer-Sitzung ist beendet. Bitte neu starten.");
          return;
        }
        throw e;
      }
      if (!n.lead || !n.nummer) {
        setzeDialer({ sitzung: n.sitzung, pausiert: true, meldung: n.meldung ?? "Keine weiteren Leads.", aktuell: n.lead ? n : { ...n, lead: dialerRef.current?.aktuell?.lead ?? null }, vorgeladen: null });
        return;
      }
      setzeDialer({ sitzung: n.sitzung, pausiert: false, meldung: null, aktuell: n, vorgeladen: null });
      if (n.lead.id !== vor?.id) navigate(leadPfad(n.lead.id));
      await anrufen(n.nummer, n.lead.id, n.contact?.id ?? undefined, {
        leadName: n.lead.name,
        kontaktName: n.contact?.name ?? null,
        dialerSitzung: n.sitzung.id,
        klingeldauer: n.sitzung.klingeldauer,
      });
    } catch (e) {
      const d = dialerRef.current ?? d0;
      setzeDialer({ ...d, pausiert: true, meldung: fehlerText(e) });
    } finally {
      weiterLaeuft.current = false;
    }
  }, [abschluss, anrufen, navigate, notizSpeichern, setzeAnruf, setzeDialer]);

  const preflightStarten = useCallback(async () => {
    setPreflight({ laeuft: true, ergebnis: null, fehler: null });
    try {
      const ergebnis: PreflightErgebnis = await engine().preflight();
      try {
        await tel.preflightSpeichern(ergebnis);
      } catch {
        /* Anzeige trotzdem */
      }
      setPreflight({ laeuft: false, ergebnis, fehler: ergebnis.ok ? null : ergebnis.warnings[0] ?? "Leitungstest fehlgeschlagen." });
      void neuLaden();
    } catch (e) {
      setPreflight({ laeuft: false, ergebnis: null, fehler: fehlerMitHilfe(e) });
    }
  }, [engine, neuLaden]);

  const dialerStarten = useCallback(
    async (smartViewId: string, optionenRoh: DialerStartOptionen | number | null = {}) => {
      const optionen: DialerStartOptionen = typeof optionenRoh === "number" ? { klingeldauer: optionenRoh, modus: "weiter" } : (optionenRoh ?? {});
      setPanelOffen(true); // Leiste zeigen (Leitungstest, Fehler), bis die Sitzung steht
      setFehler(null);
      try {
        if (!info?.preflight_heute && !preflight.ergebnis) {
          await preflightStarten();
          const s = await tel.status().catch(() => null);
          if (s) setInfo(s);
          const pf = s?.preflight;
          if (!pf || !pf.ok || pf.quality === "degraded") {
            setFehler("Leitungstest heute: schlecht oder fehlgeschlagen. Headset und Netz prüfen. Nochmal „Anrufen“ wählt trotzdem.");
            return;
          }
        }
        const { sitzung } = await tel.dialerStarten(smartViewId, optionen);
        setzeDialer({ sitzung, pausiert: false, meldung: null, aktuell: null, vorgeladen: null });
        await dialerWeiter();
      } catch (e) {
        setFehler(fehlerText(e));
      } finally {
        setPanelOffen(false);
      }
    },
    [info?.preflight_heute, preflight.ergebnis, preflightStarten, setzeDialer, dialerWeiter],
  );
  useEffect(() => {
    starter = dialerStarten;
    return () => {
      if (starter === dialerStarten) starter = null;
    };
  }, [dialerStarten]);

  const dialerPause = useCallback(
    (an: boolean) => {
      const d = dialerRef.current;
      if (d) setzeDialer({ ...d, pausiert: an, meldung: an ? "Power Dialer pausiert." : null });
    },
    [setzeDialer],
  );

  const dialerBeenden = useCallback(async () => {
    const d = dialerRef.current;
    setzeDialer(null);
    if (d) {
      try {
        await tel.dialerEnde(d.sitzung.id);
      } catch {
        /* Sitzung ist ohnehin weg */
      }
    }
  }, [setzeDialer]);

  // Abmelden: alles zurücksetzen
  useEffect(() => {
    if (nutzer) return;
    engineRef.current?.zerstoeren();
    engineRef.current = null;
    setGeraetBereit(false);
    setzeAnruf(null);
    setPhase(null);
    setNacharbeit(null);
    setzeDialer(null);
    setInfo(null);
    setzeMikrofonDb(null);
  }, [nutzer, setzeAnruf, setzeDialer]);

  useEffect(() => () => engineRef.current?.zerstoeren(), []);

  // Desktop-App: Energiesparsperre und Rückfrage vor dem Beenden gelten, solange ein Gespräch läuft.
  const gespraechLaeuft = !!anruf;
  useEffect(() => {
    const d = window.crmDesktop;
    if (!d) return;
    void Promise.resolve(d.anrufAktiv(gespraechLaeuft)).catch(() => undefined);
    return () => {
      void Promise.resolve(d.anrufAktiv(false)).catch(() => undefined);
    };
  }, [gespraechLaeuft]);

  // Desktop-App: nach Ruhezustand oder Entsperren Telefonie-Stand neu lesen und Telefon neu anmelden.
  // Das SDK allein merkt einen toten WebSocket erst spät: zusätzlich Status neu lesen und das Gerät neu anmelden.
  // Die App ruft sofort und nach 6 s; der Plan macht daraus einen Versuch (erzwungen höchstens alle 20 s).
  useEffect(
    () =>
      window.crmDesktop?.beiAufwachen(() => {
        void neuLadenBehalten();
        registrierenAnfordern({ erzwingen: true });
      }),
    [neuLadenBehalten, registrierenAnfordern],
  );

  const grund = anruf ? "Es läuft schon ein Anruf." : grundAus(info, !!nutzer);
  const basis: TelefonieZustand = !nutzer
    ? "nicht_eingerichtet"
    : !info
      ? "initialisiert"
      : grundAus(info, true)
        ? "nicht_eingerichtet"
        : "bereit";
  const status: TelefonieZustand = phase ?? basis;

  const wert = useMemo<TelefonieSchnittstelle>(
    () => ({
      status,
      grund,
      aktiverAnruf: anruf,
      fehler,
      anrufen,
      auflegen,
      stumm,
      info,
      warnungen,
      pegel,
      nacharbeit,
      beendetSchliessen,
      ergebnisSetzen,
      notiz,
      setNotiz,
      tasten,
      aufsHandy,
      neuVerbinden,
      preflight,
      preflightStarten,
      dialer,
      dialerStarten,
      dialerWeiter,
      dialerPause,
      dialerBeenden,
      panelOffen,
      setPanelOffen,
      neuLaden,
      geraeteSetzen,
      geraetBereit,
      fehlerZuruecksetzen: () => setFehler(null),
      eingehend,
      eingehendAnnehmen,
      eingehendAblehnen,
      eingehendIgnorieren,
    }),
    [
      status, grund, anruf, fehler, anrufen, auflegen, stumm, info, warnungen, pegel, nacharbeit, beendetSchliessen, ergebnisSetzen, notiz, setNotiz, tasten, aufsHandy,
      neuVerbinden, preflight, preflightStarten, dialer, dialerStarten, dialerWeiter, dialerPause, dialerBeenden,
      panelOffen, neuLaden, geraeteSetzen, geraetBereit, eingehend, eingehendAnnehmen, eingehendAblehnen, eingehendIgnorieren,
    ],
  );

  return (
    <TelefonieKontext.Provider value={wert}>
      {children}
    </TelefonieKontext.Provider>
  );
}

export function useTelefonie(): TelefonieSchnittstelle {
  return useContext(TelefonieKontext);
}

export { textZuHtml };
